import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  List,
  Modal,
  Result,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import {
  useUpdEvents,
  useUpdGetDocument,
  useUpdReject,
  useUpdReparse,
  useUpdResolveAgain,
} from '@/api/generated/endpoints/upd/upd';
import type {
  IssueOut,
  RawLine,
  UpdDocumentDetail,
  UpdEventOut,
  UpdStatus,
} from '@/api/generated/model';
import { ApiProblem } from '@/api/http';
import { useHasRole } from '@/auth/useHasRole';
import { UpdStatusTag } from '@/components/StatusTag';
import { problemMessage } from '@/i18n/problem';
import { formatDecimal } from '@/lib/decimal';
import { invalidatePath } from '@/lib/queryCache';
import { useIsMobile } from '@/lib/useIsMobile';
import { IN_PROGRESS } from '@/lib/updStatus';
import { IssueResolver } from '@/pages/upd/IssueResolver';

const DATE = 'DD.MM.YYYY';
const POLL_MS = 2_000;

/** Страница проверки УПД: шапка, проблемы, строки; статус обновляется сам, пока идёт разбор. */
export function UpdDocumentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const id = Number(useParams().docId);
  const detail = useUpdGetDocument(id, {
    query: {
      enabled: Number.isInteger(id),
      refetchInterval: (q) =>
        q.state.data && IN_PROGRESS.includes(q.state.data.status) ? POLL_MS : false,
    },
  });
  const back = (
    <Button
      type="text"
      icon={<ArrowLeftOutlined />}
      onClick={() => {
        void navigate('..');
      }}
    />
  );
  if (detail.error instanceof ApiProblem && detail.error.status === 404) {
    return <Result status="404" title={t('upd.notFound')} extra={back} />;
  }
  const d = detail.data;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        loading={detail.isPending}
        title={
          d && (
            <Space wrap>
              {back}
              {d.parsed
                ? t('upd.document', {
                    number: d.parsed.header.number,
                    date: dayjs(d.parsed.header.date).format(DATE),
                  })
                : d.original_filename}
              <UpdStatusTag status={d.status} />
            </Space>
          )
        }
        extra={d && !mobile && <Actions doc={d} large={false} />}
      >
        {d && mobile && <Actions doc={d} large />}
        {d && <Header doc={d} />}
      </Card>
      {d && <Issues doc={d} />}
      {d?.parsed && <Lines lines={d.parsed.lines} />}
      {d && <Events docId={d.id} status={d.status} />}
    </Space>
  );
}

function Header({ doc: d }: { doc: UpdDocumentDetail }) {
  const { t } = useTranslation();
  if (IN_PROGRESS.includes(d.status)) {
    return (
      <Space>
        <Spin size="small" />
        {t('upd.documentPending')}
      </Space>
    );
  }
  const h = d.parsed?.header;
  return (
    <>
      {d.error && <Alert type="error" showIcon message={d.error} style={{ marginBottom: 12 }} />}
      <Descriptions size="small" column={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2, xxl: 2 }}>
        {h && (
          <>
            <Descriptions.Item label={t('upd.seller')}>
              {h.seller.name} {h.seller.inn && `(${h.seller.inn})`}
            </Descriptions.Item>
            <Descriptions.Item label={t('upd.buyer')}>
              {h.buyer.name} {h.buyer.inn && `(${h.buyer.inn})`}
            </Descriptions.Item>
            <Descriptions.Item label={t('upd.consignee')} span={2}>
              {h.consignee_raw ?? '—'}
            </Descriptions.Item>
            {d.parsed?.totals.sum_with_vat && (
              <Descriptions.Item label={t('upd.totals')}>
                {formatDecimal(d.parsed.totals.sum_with_vat, { minPlaces: 2, maxPlaces: 2 })}
              </Descriptions.Item>
            )}
          </>
        )}
        <Descriptions.Item label={t('upd.file')}>{d.original_filename}</Descriptions.Item>
      </Descriptions>
    </>
  );
}

/** Кнопки по документу: на компьютере — в заголовке, на телефоне — крупные во всю ширину. */
function Actions({ doc: d, large }: { doc: UpdDocumentDetail; large: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canReject = useHasRole('storekeeper');
  const canReparse = useHasRole('manager');
  const [rejecting, setRejecting] = useState(false);
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const resolveAgain = useUpdResolveAgain({
    mutation: {
      onSuccess: async (next) => {
        await invalidatePath(queryClient, '/api/v1/upd-documents');
        void message.success(
          next.status === 'draft_created' ? t('resolve.draftCreated') : t('resolve.applied'),
        );
      },
      onError: (e) => void message.error(problemMessage(e)),
    },
  });
  const reparse = useUpdReparse({
    mutation: {
      onSuccess: async () => {
        await invalidatePath(queryClient, '/api/v1/upd-documents');
        void message.success(t('upd.reparsed'));
      },
      onError: (e) => void message.error(problemMessage(e)),
    },
  });
  const size = large ? ('large' as const) : 'middle';
  const buttons = (
    <>
      {d.shipment_id !== null && (
        <Button
          size={size}
          block={large}
          type="primary"
          onClick={() => {
            void navigate(`/shipments/${String(d.shipment_id)}`);
          }}
        >
          {t('upd.toShipment')}
        </Button>
      )}
      {canReject && d.status === 'needs_review' && (
        <Tooltip title={t('resolve.resolveAgainHint')}>
          <Button
            size={size}
            block={large}
            loading={resolveAgain.isPending}
            onClick={() => {
              resolveAgain.mutate({ docId: d.id, data: { version: d.version } });
            }}
          >
            {t('resolve.resolveAgain')}
          </Button>
        </Tooltip>
      )}
      {canReparse && d.status === 'failed' && (
        <Button
          size={size}
          block={large}
          loading={reparse.isPending}
          onClick={() => {
            reparse.mutate({ docId: d.id });
          }}
        >
          {t('upd.reparse')}
        </Button>
      )}
      {canReject && (d.status === 'needs_review' || d.status === 'failed') && (
        <Button
          size={size}
          block={large}
          danger
          onClick={() => {
            setRejecting(true);
          }}
        >
          {t('upd.reject')}
        </Button>
      )}
    </>
  );
  const reject = (
    <RejectModal
      doc={d}
      open={rejecting}
      onClose={() => {
        setRejecting(false);
      }}
    />
  );
  // Нет ни одной кнопки — на телефоне не оставляем пустого отступа над шапкой.
  const none =
    d.shipment_id === null &&
    !(canReject && (d.status === 'needs_review' || d.status === 'failed')) &&
    !(canReparse && d.status === 'failed');
  if (large) {
    if (none) return reject;
    return (
      <Flex vertical gap={8} style={{ marginBottom: 16 }}>
        {buttons}
        {reject}
      </Flex>
    );
  }
  return (
    <Space wrap>
      {buttons}
      {reject}
    </Space>
  );
}

function RejectModal({
  doc,
  open,
  onClose,
}: {
  doc: UpdDocumentDetail;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ reason: string }>();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const reject = useUpdReject({
    mutation: {
      onSuccess: async () => {
        await invalidatePath(queryClient, '/api/v1/upd-documents');
        void message.success(t('upd.rejected'));
        onClose();
      },
      onError: async (e) => {
        if (e instanceof ApiProblem && e.code === 'version_conflict') {
          await invalidatePath(queryClient, '/api/v1/upd-documents');
        }
        setError(problemMessage(e));
      },
    },
  });
  return (
    <Modal
      title={t('upd.rejectTitle')}
      open={open}
      onCancel={onClose}
      okText={t('upd.reject')}
      okButtonProps={{ danger: true, loading: reject.isPending }}
      onOk={() => {
        form.submit();
      }}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => {
          setError(null);
          reject.mutate({
            docId: doc.id,
            data: { version: doc.version, reason: v.reason.trim() },
          });
        }}
      >
        <Form.Item
          name="reason"
          label={t('upd.rejectReason')}
          rules={[
            { required: true, whitespace: true, min: 3, message: t('upd.rejectReasonRequired') },
          ]}
        >
          <Input.TextArea maxLength={1000} autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}

/** Что нужно решить по документу. Решения (резолюции) — день 32; здесь — список и суть. */
function Issues({ doc }: { doc: UpdDocumentDetail }) {
  const { t } = useTranslation();
  if (doc.status !== 'needs_review') return null;
  const clientPending = doc.issues.some((i) => i.code === 'CLIENT_UNRESOLVED');
  return (
    <Card title={t('upd.issues')}>
      <List<IssueOut>
        rowKey="issue_id"
        dataSource={doc.issues}
        locale={{ emptyText: t('upd.noIssues') }}
        renderItem={(i) => (
          <List.Item>
            <Space direction="vertical" size={0}>
              <Space wrap>
                <Tag color={i.severity === 'blocking' ? 'red' : 'orange'}>
                  {t(`upd.severity.${i.severity}`)}
                </Tag>
                <Typography.Text strong>
                  {t(`upd.issueCode.${i.code}`, { defaultValue: i.code })}
                </Typography.Text>
                {i.line_no !== null && (
                  <Typography.Text type="secondary">
                    {t('upd.line', { n: i.line_no })}
                  </Typography.Text>
                )}
              </Space>
              <span>{i.message}</span>
              <div style={{ marginTop: 8 }}>
                {/* key: форма держит свой ввод — у следующей проблемы на том же месте он должен быть свой */}
                <IssueResolver key={i.issue_id} doc={doc} issue={i} clientPending={clientPending} />
              </div>
            </Space>
          </List.Item>
        )}
      />
    </Card>
  );
}

function Lines({ lines }: { lines: RawLine[] }) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const money = (v: string | null) => (v ? formatDecimal(v, { minPlaces: 2, maxPlaces: 2 }) : '—');
  // Телефон (T-177): строка УПД — одной ячейкой: наименование, артикул, количество, цена и сумма.
  const mobileColumns: TableColumnsType<RawLine> = [
    {
      key: 'line',
      title: t('upd.colName'),
      render: (_, l) => (
        <Space direction="vertical" size={2}>
          <Typography.Text>
            <Typography.Text type="secondary">{l.line_no}. </Typography.Text>
            {l.name}
          </Typography.Text>
          {l.article && <Typography.Text type="secondary">{l.article}</Typography.Text>}
          <Typography.Text strong>
            {`${formatDecimal(l.qty)} ${l.uom_label ?? ''}`.trim()} × {money(l.price)} ={' '}
            {money(l.sum_with_vat)}
          </Typography.Text>
        </Space>
      ),
    },
  ];
  const columns: TableColumnsType<RawLine> = [
    { key: 'n', title: t('upd.colLine'), dataIndex: 'line_no', width: 50 },
    {
      key: 'article',
      title: t('upd.colArticle'),
      dataIndex: 'article',
      width: 150,
      render: (a: string | null) => a ?? '—',
    },
    { key: 'name', title: t('upd.colName'), dataIndex: 'name' },
    {
      key: 'qty',
      title: t('upd.colQty'),
      align: 'right',
      width: 140,
      render: (_, l) => `${formatDecimal(l.qty)} ${l.uom_label ?? ''}`.trim(),
    },
    {
      key: 'price',
      title: t('upd.colPrice'),
      align: 'right',
      width: 110,
      render: (_, l) => (l.price ? formatDecimal(l.price, { minPlaces: 2, maxPlaces: 2 }) : '—'),
    },
    {
      key: 'sum',
      title: t('upd.colSum'),
      align: 'right',
      width: 140,
      render: (_, l) =>
        l.sum_with_vat ? formatDecimal(l.sum_with_vat, { minPlaces: 2, maxPlaces: 2 }) : '—',
    },
  ];
  return (
    <Card title={t('upd.lines')}>
      <Table<RawLine>
        rowKey="line_no"
        size="small"
        columns={mobile ? mobileColumns : columns}
        dataSource={lines}
        pagination={false}
      />
    </Card>
  );
}

const LEVEL_COLOR: Record<string, string> = { info: 'default', warning: 'orange', error: 'red' };

/** Журнал разбора: загрузка, разбор, сопоставление, решения — для разбора спорных случаев. */
function Events({ docId, status }: { docId: number; status: UpdStatus }) {
  const { t } = useTranslation();
  const events = useUpdEvents(docId, {
    query: { refetchInterval: IN_PROGRESS.includes(status) ? POLL_MS : false },
  });
  return (
    <Card title={t('events.title')} size="small">
      <List<UpdEventOut>
        size="small"
        loading={events.isPending}
        dataSource={events.data ?? []}
        locale={{ emptyText: t('events.empty') }}
        renderItem={(e) => (
          <List.Item>
            <Space wrap>
              <Typography.Text type="secondary">
                {dayjs(e.created_at).format('DD.MM.YYYY HH:mm:ss')}
              </Typography.Text>
              <Tag color={LEVEL_COLOR[e.level] ?? 'default'}>{e.stage}</Tag>
              <span>{e.message}</span>
            </Space>
          </List.Item>
        )}
      />
    </Card>
  );
}
